import { ContactSelectionLight } from '@/components/contact-selection/contact-selection.light';
import { ContactSelectionWoo } from '@/components/contact-selection/contact-selection.woo';
import { ContactSelectionWooDark } from '@/components/contact-selection/contact-selection.woo-dark';
import type { ContactSelectionProps, ContactType } from '@/components/contact-selection/props';
import { ErrorSidePanel } from '@/components/errors';
import { useGetPersonalInfo } from '@/hooks/api';
import { useSendCode } from '@/hooks/api/contact-validation.hooks';
import { loadHistoryState } from '@/lib';
import { ErrorCodes, templates, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import { contactSelectionStateSchema } from '../-model/flow';
import type { CodeInputState } from '../-model/flow';

export const Route = createFileRoute('/(tv)/tv/$id/(pins)/_sheet/pins/$pin/contact-selection/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  loader: loadHistoryState(contactSelectionStateSchema, { errorCode: ErrorCodes.AccessDenied }),
});

function RouteComponent() {
  const { id, pin: pinType } = Route.useParams();
  const navigate = Route.useNavigate();
  const { pins } = Route.useLoaderData();

  const { sendCodeAsync, isSending, captcha } = useSendCode();
  const { data: personalInfo } = useGetPersonalInfo();

  const phoneAlias = personalInfo?.UsernameAliases?.find((a) => a.Type === 'Mobile')?.Username;
  const emailAlias = personalInfo?.UsernameAliases?.find((a) => a.Type === 'Email')?.Username;

  const handleContactSelected = async (contactType: ContactType, targetId: string) => {
    if (captcha.challenge && !captcha.token) return;
    await sendCodeAsync({ targetId, type: contactType, captcha });
    void navigate({
      to: '/tv/$id/pins/$pin/code-input',
      params: { id, pin: pinType },
      state: { contactType, targetId, pins } as CodeInputState,
    });
  };

  const props: ContactSelectionProps = {
    translations: {
      phone: 'Common.ContactValidation.ContactSelection.Phone',
      email: 'Common.ContactValidation.ContactSelection.Email',
    },
    phoneAlias,
    emailAlias,
    onContactSelected: (ct, tid) => void handleContactSelected(ct, tid),
    isSending,
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: ContactSelectionProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <ContactSelectionWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <ContactSelectionWoo {...props} />,
      },
    ],
    defaultValue: <ContactSelectionLight {...props} />,
  };
}
